
















import {
  computed,
  defineComponent,
  inject,
  PropType,
  toRefs,
  useContext
} from '@nuxtjs/composition-api'

import DChip from '~/components/molecules/DS2/DChip'
import { SearchServiceCondition } from '~/stub/apigateway/service/search_pb'
import {
  ChangeConditionObject,
  ConditionOptionCommon,
  SelectedSearchCondition
} from '~/types/services/search'
import { serviceSearchOptionValue } from '~/stub/apigateway/master/master_pb'
import { SERVICE_KIND_VALUE } from '~/constants/search'

const selectedSearchServiceConditions = defineComponent({
  name: 'SelectedSearchServiceConditions',
  components: {
    DChip
  },
  props: {
    conditionOptionsCommon: {
      type: Array as PropType<ConditionOptionCommon[]>,
      default: () => []
    },
    condition: {
      type: Object as PropType<SearchServiceCondition.AsObject>,
      default: () => ({})
    }
  },
  setup(props) {
    const { $util } = useContext()
    const setOptionAttributes = inject<
      (condition: ChangeConditionObject, emitChangeEvent?: boolean) => void
    >('setOptionAttributes')
    const { conditionOptionsCommon, condition } = toRefs(props)

    // 選択済みの絞り込み条件
    const selectedCondition = computed(() => {
      const options: SelectedSearchCondition[] = []
      conditionOptionsCommon.value.forEach(conditionOptionCommon => {
        if (conditionOptionCommon.option.value) {
          options.push(
            ...conditionOptionCommon.master.valuesList.flatMap(
              optionValue =>
                optionValue.value === conditionOptionCommon.option.value
                  ? {
                      key: conditionOptionCommon.master.key,
                      id: optionValue.id,
                      name: optionValue.fullName,
                      value: optionValue.value,
                      valuesList: [],
                      allowsMultipleSelect: conditionOptionCommon.master.type,
                      relatedCategoryId: conditionOptionCommon.option.relatedCategoryId
                    }
                  : []
            )
          )
        } else if (conditionOptionCommon.option.valuesList?.length) {
          conditionOptionCommon.option.valuesList.forEach(v => {
            options.push(
              ...conditionOptionCommon.master.valuesList.flatMap(
                optionValue =>
                  optionValue.value === v
                    ? {
                        key: conditionOptionCommon.master.key,
                        id: optionValue.id,
                        name: optionValue.fullName,
                        value: optionValue.value,
                        valuesList: conditionOptionCommon.option.valuesList,
                        allowsMultipleSelect: conditionOptionCommon.master.type,
                        relatedCategoryId: conditionOptionCommon.option.relatedCategoryId
                      }
                    : []
              )
            )
          })
        } else if (conditionOptionCommon.master.key === 'provider_level_ids') {
          if (!condition.value.providerLevelIdsList.length) return
          condition.value.providerLevelIdsList.forEach((providerLevel: number) => {
            const providerLevelIdString = String(providerLevel)
            const optionValue = conditionOptionCommon.master.valuesList.find(
              v => v.valueKey === 'provider_level_ids' && v.value === providerLevelIdString
            )
            if (optionValue) {
              // providerLevelIdsがnumberの配列で返ってくるのstring配列に変換してあげる
              const providerLevelIdsList = condition.value.providerLevelIdsList.map(v => String(v))
              const param = getSpecialtyConditionParam(
                optionValue,
                conditionOptionCommon,
                providerLevelIdString,
                providerLevelIdsList
              )
              options.push(param)
            }
          })
        } else if (conditionOptionCommon.master.key === 'provider_status') {
          if (
            !condition.value.reservationAvailableFlag &&
            !condition.value.providerStatusesList.length
          )
            return
          // 出品者状況:予約受付中フラグ
          if (condition.value.reservationAvailableFlag) {
            const optionValue = conditionOptionCommon.master.valuesList.find(
              v =>
                v.valueKey === 'reservation_available_flag' &&
                v.value === condition.value.reservationAvailableFlag
            )
            if (optionValue) {
              const param = getSpecialtyConditionParam(
                optionValue,
                conditionOptionCommon,
                condition.value.reservationAvailableFlag,
                []
              )
              options.push(param)
            }
          }

          // 出品者状況:今すぐ相談可能フラグ
          if (condition.value.providerStatusesList.length) {
            condition.value.providerStatusesList.forEach((providerStatus: string) => {
              const optionValue = conditionOptionCommon.master.valuesList.find(
                v => v.valueKey === 'provider_statuses' && v.value === providerStatus
              )
              if (optionValue) {
                const param = getSpecialtyConditionParam(
                  optionValue,
                  conditionOptionCommon,
                  '',
                  condition.value.providerStatusesList
                )
                options.push(param)
              }
            })
          }
        } else if (conditionOptionCommon.master.key === 'additional_options') {
          if (
            !condition.value.customizeFlag &&
            !condition.value.shipmentFlag &&
            !condition.value.providerStatusesList.length
          )
            return
          // 見積りフラグ
          if (condition.value.customizeFlag) {
            const optionValue = conditionOptionCommon.master.valuesList.find(
              v => v.valueKey === 'customize_flag' && v.value === condition.value.customizeFlag
            )

            if (optionValue) {
              const param = getSpecialtyConditionParam(
                optionValue,
                conditionOptionCommon,
                condition.value.customizeFlag,
                []
              )
              options.push(param)
            }
          }
          // 物品配送フラグ
          if (condition.value.shipmentFlag) {
            const optionValue = conditionOptionCommon.master.valuesList.find(
              v => v.valueKey === 'shipment_flag' && v.value === condition.value.shipmentFlag
            )
            if (optionValue) {
              const param = getSpecialtyConditionParam(
                optionValue,
                conditionOptionCommon,
                condition.value.shipmentFlag,
                []
              )
              options.push(param)
            }
          }
          // PRO認定・ビデオチャット可能フラグ
          if (condition.value.providerStatusesList.length) {
            condition.value.providerStatusesList.forEach((providerStatus: string) => {
              const optionValue = conditionOptionCommon.master.valuesList.find(
                v => v.valueKey === 'provider_statuses' && v.value === providerStatus
              )
              if (optionValue) {
                const param = getSpecialtyConditionParam(
                  optionValue,
                  conditionOptionCommon,
                  '',
                  condition.value.providerStatusesList
                )
                options.push(param)
              }
            })
          }
        } else if (conditionOptionCommon.master.key === 'identification_status') {
          if (!condition.value.identificationFlag && !condition.value.ndaConclusionFlag) return
          // 本人確認済みフラグ
          if (condition.value.identificationFlag) {
            const optionValue = conditionOptionCommon.master.valuesList.find(
              v =>
                v.valueKey === 'identification_flag' &&
                v.value === condition.value.identificationFlag
            )
            if (optionValue) {
              const param = getSpecialtyConditionParam(
                optionValue,
                conditionOptionCommon,
                condition.value.identificationFlag,
                []
              )
              options.push(param)
            }
          }
          // 機密保持契約集結済みフラグ
          if (condition.value.ndaConclusionFlag) {
            const optionValue = conditionOptionCommon.master.valuesList.find(
              v =>
                v.valueKey === 'nda_conclusion_flag' &&
                v.value === condition.value.ndaConclusionFlag
            )
            if (optionValue) {
              const param = getSpecialtyConditionParam(
                optionValue,
                conditionOptionCommon,
                condition.value.ndaConclusionFlag,
                []
              )
              options.push(param)
            }
          }
        } else if (conditionOptionCommon.master.key === 'service_kind') {
          // サービス種類が未設定の時は何もしない
          if (!condition.value.serviceKind) return
          const optionValue = conditionOptionCommon.master.valuesList.find(
            v => v.value === condition.value.serviceKind
          )
          if (optionValue) {
            const param = getSpecialtyConditionParam(
              optionValue,
              conditionOptionCommon,
              condition.value.serviceKind,
              []
            )
            options.push(param)
          }
        } else if (conditionOptionCommon.master.key === 'service_price_range') {
          // TODO: category_v2 あとでリファクタリングしたい
          // サービス価格の絞り込み状態の表示
          // 価格の状態はconditionにしか保持していないため、conditionのpriceMinとpriceMaxを使用する

          // サービス価格が入力されている時は価格ラベルを表示
          if (condition.value.priceMin || condition.value.priceMax) {
            const priceLabel = genPriceLabel(
              condition.value.priceMin,
              condition.value.priceMax,
              conditionOptionCommon.master.name
            )
            options.push({
              key: conditionOptionCommon.master.key,
              id: conditionOptionCommon.master.id,
              name: priceLabel,
              value: conditionOptionCommon.option.value,
              valuesList: conditionOptionCommon.option.valuesList,
              allowsMultipleSelect: conditionOptionCommon.master.type,
              relatedCategoryId: conditionOptionCommon.option.relatedCategoryId
            })
          }
        } else if (conditionOptionCommon.master.key === 'unit_price') {
          // 文字単価の絞り込み状態の表示
          const minPrice = conditionOptionCommon.option.min?.value
          const maxPrice = conditionOptionCommon.option.max?.value

          // 文字単価が入力されている時は価格ラベルを表示
          if (minPrice || maxPrice) {
            const priceLabel = genPriceLabel(minPrice, maxPrice, conditionOptionCommon.master.name)
            options.push({
              key: conditionOptionCommon.master.key,
              id: conditionOptionCommon.master.id,
              name: priceLabel,
              value: conditionOptionCommon.option.value,
              valuesList: conditionOptionCommon.option.valuesList,
              allowsMultipleSelect: conditionOptionCommon.master.type,
              relatedCategoryId: conditionOptionCommon.option.relatedCategoryId
            })
          }
        }
      })
      return options
    })

    /**
     * 特別対応が必要なケースの検索パラメータで絞り込み条件のラベル表示に必要な情報を取得する
     */
    const getSpecialtyConditionParam = (
      optionValue: serviceSearchOptionValue.AsObject,
      conditionOptionCommon: ConditionOptionCommon,
      value: string,
      valuesList: string[]
    ) => {
      return {
        key: optionValue.valueKey,
        id: conditionOptionCommon.master.id,
        name: optionValue.fullName,
        value,
        valuesList,
        allowsMultipleSelect: conditionOptionCommon.master.type,
        relatedCategoryId: conditionOptionCommon.option.relatedCategoryId
      }
    }

    /**
     * 価格表記のラベルを取得する
     */
    const genPriceLabel = (minPrice: string, maxPrice: string, conditionName: string): string => {
      if (minPrice && maxPrice) {
        if (minPrice === maxPrice) {
          return `${conditionName}：${$util.numberWithDelimiter(minPrice)}円`
        }
        return `${conditionName}：${$util.numberWithDelimiter(
          minPrice
        )}円〜${$util.numberWithDelimiter(maxPrice)}円`
      }
      if (minPrice) {
        return `${conditionName}：${$util.numberWithDelimiter(minPrice)}円〜`
      }
      if (maxPrice) {
        return `${conditionName}：〜${$util.numberWithDelimiter(maxPrice)}円`
      }
      return ''
    }

    /**
     * 絞り込み検索を解除する
     * @param item
     */
    const clearItem = (item: SelectedSearchCondition): void => {
      // サービス価格の時はpriceMinとpriceMaxのキーを削除する
      if (item.key === 'service_price_range') {
        // NOTE: 多重にルータープッシュしないように価格の下限解除する時には親コンポーネントにemitしない。
        setOptionAttributes(
          { key: 'priceMin', value: [], categoryId: item.relatedCategoryId },
          false
        )
        setOptionAttributes({ key: 'priceMax', value: [], categoryId: item.relatedCategoryId })
        return
      }

      // 文字単価の時はunitPriceMinとunitPriceMaxのキーを削除する
      if (item.key === 'unit_price') {
        // NOTE: 多重にルータープッシュしないように価格の下限解除する時には親コンポーネントにemitしない。
        setOptionAttributes(
          { key: 'unitPriceMin', value: [], categoryId: item.relatedCategoryId },
          false
        )
        setOptionAttributes({ key: 'unitPriceMax', value: [], categoryId: item.relatedCategoryId })
        return
      }

      // サービスの種類でビデオと電話の時には文字単価の情報をリセットする
      if (item.key === 'service_kind') {
        if (item.value === SERVICE_KIND_VALUE.PHONE || item.value === SERVICE_KIND_VALUE.VIDEO) {
          setOptionAttributes(
            { key: 'unitPriceMin', value: [], categoryId: item.relatedCategoryId },
            false
          )
          setOptionAttributes(
            { key: 'unitPriceMax', value: [], categoryId: item.relatedCategoryId },
            false
          )
        }
        setOptionAttributes({ key: 'service_kind', value: [], categoryId: item.relatedCategoryId })
        return
      }

      if (item.allowsMultipleSelect === 'checkbox') {
        const newValuesList = (() => {
          // 出品者ランク
          if (item.key === 'provider_level_ids') {
            return getProviderLevelValueWithoutClearItem(item)
          }
          // 出品者状況の「今すぐ相談可能/予約受付中」と追加オプションの「PRO認定」
          if (item.key === 'provider_statuses') {
            return getProviderStatusesValueWithoutClearItem(item)
          }
          return item.valuesList.filter(v => v !== item.value)
        })()
        setOptionAttributes({
          key: item.key,
          value: newValuesList,
          categoryId: item.relatedCategoryId
        })
      } else {
        setOptionAttributes({ key: item.key, value: [], categoryId: item.relatedCategoryId })
      }
    }

    // 削除する項目を除いた出品者ランクのデータを返す
    const getProviderLevelValueWithoutClearItem = (item: SelectedSearchCondition) => {
      return condition.value.providerLevelIdsList
        .map(providerLevel => String(providerLevel))
        .filter(v => v !== item.value)
    }

    // 削除する項目を除いた出品者状況とPRO認定のデータを返す
    const getProviderStatusesValueWithoutClearItem = (item: SelectedSearchCondition) => {
      return item.valuesList.filter(itemValue => {
        return !condition.value.providerStatusesList.includes(itemValue)
      })
    }

    return {
      selectedCondition,
      clearItem
    }
  }
})

export default selectedSearchServiceConditions
